import React, { Fragment, useEffect, useState } from "react";

import { Redirect } from "react-router";
import { isEqual } from "lodash";
import { useForm } from "react-hook-form/dist/react-hook-form.ie11";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import { CoveredRegion, FranchiseType } from "@hubcapp/types";
import { Discount, ProductCategory, ProductCategoryType } from "@hubcapp/types";
import { UnsavedChangesPromptModal } from "@hubcapp/components";
import { getDefaultFranchiseProductCategory } from "@hubcapp/utils";
import { selectSelectedFranchise } from "@hubcapp/state";
import { useQueryCoverage } from "@hubcapp/hooks";
import { useTealium } from "@hubcapp/context";

import { Area, AreaPrice, District } from "../types";
import { ManageAreaFormFields, ManageAreaStateProviderProps } from "./types";

import {
  getSectorIdsFromDistricts,
  mapAreaStateToAreaCoverageMutation,
  transformAreaToFormValues,
  transformCoverageToArea,
} from "./helpers";
import { validateAreaPricing } from "../helpers";
import ManageArea from "./ManageArea";
import useMutationDeleteArea from "./hooks/useMutationDeleteArea";
import useMutationUpdateAreaCoverage from "./hooks/useMutationUpdateAreaCoverage";

const ManageAreaStateProvider: React.FC<ManageAreaStateProviderProps> = ({
  id,
}) => {
  const { push } = useHistory();
  const [area, setArea] = useState<Area>();
  const [cache, setCache] = useState<Area>();
  const [incomplete, setIncomplete] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pricesChanged, setPricesChanged] = useState<boolean>(false);
  const selectedFranchise = useSelector(selectSelectedFranchise);
  const history = useHistory();

  if (!selectedFranchise) {
    return <Redirect to="/settings/areas-and-prices/" />;
  }

  const defaultProductCategory = selectedFranchise?.proposition?.productCategories?.find(
    category =>
      category.type === getDefaultFranchiseProductCategory(selectedFranchise),
  ) as ProductCategory;

  useEffect(() => {
    if (!area || isEqual(area, cache)) return;
    setIncomplete(true);
    form.setValue("sectors", getSectorIdsFromDistricts(area.districts));
    form.setValue(
      "discounts",
      area.discounts ? area.discounts.map(discount => discount?.id) : [],
    );
    form.setValue("pricing", area.pricing);
    return;
  }, [area]);

  const onMutationCompleted = () => {
    setIncomplete(false);
    setSubmitting(false);
    push(`/settings/areas-and-prices/`);
  };

  const [updateAreaCoverage] = useMutationUpdateAreaCoverage({
    onCompleted: onMutationCompleted,
  });

  const form = useForm<ManageAreaFormFields>({
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { dirty } = form.formState;
  const { trackEvent } = useTealium();

  const setFormValues = (area: Area) => {
    setArea(area);
    form.reset(transformAreaToFormValues(area));

    form.register({ name: "sectors" });
    form.register({ name: "discounts" });
    form.register({ name: "pricing" });
  };

  const onQueryCompleted = (data: { coverage: CoveredRegion }) => {
    if (!data) return;
    const area = transformCoverageToArea(
      data.coverage,
      defaultProductCategory.type,
    );
    setCache(area);
    setFormValues(area);
  };
  const { loading } = useQueryCoverage(id, onQueryCompleted, "network-only");

  const handleDistrictsChange = (districts: District[]) => {
    setIncomplete(true);
    if (area) {
      setArea({
        ...area,
        districts,
      });
    }
  };

  const handleDiscountsChange = (discounts: Discount[]) => {
    setIncomplete(true);
    if (area) {
      setArea({
        ...area,
        discounts,
      });
    }
  };

  const handlePriceChange = (updatedAreaPrice: AreaPrice) => {
    const newArea = {
      ...area,
      pricing: area?.pricing?.map((areaPrice: AreaPrice) => {
        if (areaPrice.id === updatedAreaPrice.id) {
          return validateAreaPricing(updatedAreaPrice);
        }

        return areaPrice;
      }),
    } as Area;
    setArea(newArea);
    form.setValue("pricing", newArea.pricing);
    setPricesChanged(true);
    setIncomplete(true);
  };

  const handleDeleteCompleted = () => {
    push("/settings/areas-and-prices");
  };

  const handleDiscard = () => {
    trackEvent({
      eventCategory: "Areas & prices - Edit My District",
      eventAction: "Button Click",
      eventLabel: "Discard",
    });
    cache && setFormValues(cache);
    cache && setArea(cache);
  };

  const handleSubmit = () => {
    if (!area || submitting) return;
    setSubmitting(true);
    trackEvent({
      eventCategory: "Areas & prices - Edit My District",
      eventAction: "Button Click",
      eventLabel: "Save",
    });

    const areaCoverage = mapAreaStateToAreaCoverageMutation(area);

    updateAreaCoverage({
      variables: {
        ...areaCoverage,
      },
    });
  };

  const onDeleteClick = () => {
    trackEvent({
      eventCategory: "Areas & prices - Edit My District",
      eventAction: "Button Click",
      eventLabel: "Delete This Area",
    });
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  const onDeleteCancel = () => {
    trackEvent({
      eventCategory: "Areas & prices - Edit My District",
      eventAction: "Button Click",
      eventLabel: "I Want To Keep This Area",
    });
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  const [deleteArea, { loading: deleting }] = useMutationDeleteArea({
    onCompleted: () => {
      setSubmitting(false);
    },
  });

  const handleDelete = () => {
    if (((dirty || pricesChanged) && incomplete) || !area?.coverageId) {
      return;
    }

    setSubmitting(true);

    trackEvent({
      eventCategory: "Areas & prices - Edit My District",
      eventAction: "Button Click",
      eventLabel: "Confirm Delete This Area",
    });

    deleteArea({
      variables: {
        id: area?.coverageId,
      },
    });
    setIsDeleteModalOpen(false);
    setIsDeleteModalOpen(false);
    history.go(0);
  };

  const handleUnsavedChangesSaveClick = () => {
    trackEvent({
      eventCategory: "Areas & prices - Edit My District",
      eventAction: "Button Click",
      eventLabel: "Unsaved - Save Changes",
    });
    handleSubmit();
  };

  const handleDiscardClick = () => {
    trackEvent({
      eventCategory: "Areas & prices - Edit My District",
      eventAction: "Button Click",
      eventLabel: "Unsaved - Discard",
    });
    setIsDeleteModalOpen(false);
    form.reset();
  };

  const dirtyCheck = (dirty || pricesChanged) && incomplete;

  return (
    <Fragment>
      <UnsavedChangesPromptModal
        showPrompt={dirtyCheck}
        onSaveClick={form.handleSubmit(handleUnsavedChangesSaveClick)}
        onDiscardClick={handleDiscardClick}
      />
      <ManageArea
        {...area}
        loading={loading}
        submitting={submitting || deleting}
        dirty={dirty || pricesChanged}
        canEditDistricts={
          area && selectedFranchise.proposition.type !== FranchiseType.Dit
        }
        canEditPrices={area && area.type === ProductCategoryType.PreTest}
        onDistrictsChange={handleDistrictsChange}
        onDiscountsChange={handleDiscountsChange}
        onPriceChange={handlePriceChange}
        isDeleteModalOpen={isDeleteModalOpen}
        onDeleteClick={onDeleteClick}
        onDeleteCancel={onDeleteCancel}
        onDeleteCompleted={handleDeleteCompleted}
        onDiscard={handleDiscard}
        onSubmit={form.handleSubmit(handleSubmit)}
        onDelete={handleDelete}
        proposition={selectedFranchise.proposition}
        propositionId={selectedFranchise.proposition.id}
        productCategoryId={defaultProductCategory.id}
        priceError={area?.pricing?.some(
          (instructorPrice: AreaPrice) => instructorPrice.error === true,
        )}
      />
    </Fragment>
  );
};

export default ManageAreaStateProvider;
